import React from 'react';
import { Spinner } from '@blueprintjs/core';
import { observable } from 'mobx';
import { observer, inject } from 'mobx-react';
import { Api } from 'services/Api';
import moment from 'moment-timezone';
import { formatTime } from '../../helpers/time';
import { TimeLogPills } from 'components/Job/recordPills';
import JobEmpty from 'components/Job/JobEmpty';

const minutesOf = (a) =>
  a.start_time && a.end_time ? Math.max(0, moment(a.end_time).diff(moment(a.start_time), 'minutes')) : 0;

// "8 hrs", "8 hrs 30 min", "45 min".
const hoursLabel = (minutes) => {
  if (!minutes) return '';
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  return [hours && `${hours} ${hours === 1 ? 'hr' : 'hrs'}`, mins && `${mins} min`].filter(Boolean).join(' ');
};

const dayLabel = (date) => {
  const day = moment(date);
  if (day.isSame(moment(), 'day')) return 'Today';
  if (day.isSame(moment().subtract(1, 'day'), 'day')) return 'Yesterday';
  return day.format(day.isSame(moment(), 'year') ? 'ddd, MMM D' : 'ddd, MMM D, YYYY');
};

// "1:45 – 9:45 PM", or "7:05 PM – Fri 6:05 AM" when it runs past midnight.
const rangeLabel = (start, end) => {
  if (!start) return '';
  const s = moment(start);
  if (!end) return s.format('h:mm A');
  const e = moment(end);
  if (!s.isSame(e, 'day')) return `${s.format('h:mm A')} – ${e.format('ddd h:mm A')}`;
  return s.format('A') === e.format('A') ? `${s.format('h:mm')} – ${e.format('h:mm A')}` : `${s.format('h:mm A')} – ${e.format('h:mm A')}`;
};

const initialsOf = (name = '') =>
  String(name || '')
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((word) => word[0].toUpperCase())
    .join('');

@inject('jobStore') @observer
class TimeLogs extends React.Component {

  componentDidMount() {
    let { 
      job,
      jobStore
    } = this.props;
    jobStore.loadJobTimesheets(job);
  }

  componentDidUpdate(prevProps) {
    const previousId = prevProps.job?.attributes?.id;
    const nextId = this.props.job?.attributes?.id;
    if (nextId && nextId !== previousId) {
      this.props.jobStore.loadJobTimesheets(this.props.job);
    }
  }

  render() {
    let { 
      jobStore: {
        timeLogs: {
          objects, 
          isLoading
        }
      }
    } = this.props;
    if (isLoading) {
      return <div className="spinner-center"><Spinner size='25' /></div>
    }
    if (objects.length === 0) {
      return (
        <JobEmpty kind="time-logs" />
      )
    }
    // By day (newest first), each with its total; rows read "who, when, how long".
    const days = [];
    objects
      .slice()
      .sort((x, y) => new Date(y.attributes.start_time) - new Date(x.attributes.start_time))
      .forEach((timelog) => {
        const key = moment(timelog.attributes.start_time).format('YYYY-MM-DD');
        let day = days[days.length - 1];
        if (!day || day.key !== key) {
          day = { key, start: timelog.attributes.start_time, minutes: 0, logs: [] };
          days.push(day);
        }
        day.minutes += minutesOf(timelog.attributes);
        day.logs.push(timelog);
      });

    return (
      <div className="tl-days">
        {days.map((day) => (
          <section key={day.key} className="tl-day" aria-label={moment(day.start).format('dddd, MMMM D')}>
            <h3 className="tl-day__head">
              <span>{dayLabel(day.start)}</span>
              <span className="tl-day__total">{hoursLabel(day.minutes)}</span>
            </h3>
            <ul className="tl-rows">
              {day.logs.map((timelog) => {
                const a = timelog.attributes;
                return (
                  <li key={timelog.id} className="tl-row">
                    <span className="tl-row__avatar" aria-hidden="true">{initialsOf(a.technician_name)}</span>
                    <span className="tl-row__who">
                      <span className="tl-row__name">{a.technician_name}</span>
                      <span className="tl-row__when" title={`${formatTime(a.start_time)} – ${formatTime(a.end_time)}`}>{rangeLabel(a.start_time, a.end_time)}</span>
                    </span>
                    <span className="tl-row__hours">{hoursLabel(minutesOf(a)) || a.formatted_duration}</span>
                    <span className="tl-row__pills"><TimeLogPills attributes={a} /></span>
                  </li>
                );
              })}
            </ul>
          </section>
        ))}
      </div>
    )
  }
}

export default TimeLogs;